import { useEffect, useState } from 'react';
import {
  Anchor,
  Button,
  ButtonGroup,
  FieldSet,
  FormField,
  Heading,
  Label,
  LabeledControl,
  Legend,
  Modal,
  Paragraph,
  Radio,
  Text,
  Textarea,
  TextButton,
} from '@zillow/constellation-10';
import getBaseUrlByPersona from './../../../utils/getBaseUrlByPersona';
import usePathsManifest from './../../../hooks/usePathsManifest';
import { ReportConversationReason } from '../../../types/GraphqlTypes';
import getFormattedBrandName from '../../../utils/getFormattedBrandName';
import type { Brand } from '../../../types/brand';
import type { Persona } from '../../../types/persona';

interface ReportConversationArgs {
  reason: ReportConversationReason;
  comments?: string;
}

export type ReportConversationModalProps = {
  brand?: Brand;
  isImpersonator: boolean;
  isOpen?: boolean;
  name: string;
  onCancelClick(): void;
  onReportClick({ reason, comments }: ReportConversationArgs): void;
  persona?: Persona;
};

const REPORT_REASON_SELECTIONS = [
  { label: 'Spam', value: ReportConversationReason.Spam },
  { label: 'Discriminatory', value: ReportConversationReason.DiscriminatoryOrAbusive },
  { label: 'Other', value: ReportConversationReason.Other },
] as const;

export default function ReportConversationModal({
  brand = 'zillow',
  isImpersonator,
  isOpen = true,
  name,
  onCancelClick,
  onReportClick,
  persona = 'landlord',
}: ReportConversationModalProps) {
  const [showReportReasonSelections, setShowReportReasonSelections] = useState(true);
  const [showAdditionalText, setShowAdditionalText] = useState(false);
  const [selectedReason, setSelectedReason] = useState<ReportConversationReason | null>(null);
  const [comments, setComments] = useState<string>('');
  const baseUrl = getBaseUrlByPersona(persona);
  const { paths } = usePathsManifest(baseUrl, brand);
  const displayName = name || 'this person';
  const formattedBrandName = getFormattedBrandName(brand);

  useEffect(() => {
    if (!isOpen) {
      // Reset all state
      setShowReportReasonSelections(true);
      setSelectedReason(null);
      setShowAdditionalText(false);
      setComments('');
    }
  }, [isOpen]);

  const handleCancelClick = onCancelClick;

  const handleReportClick = () => {
    if (!selectedReason) return;

    onReportClick({
      reason: selectedReason,
      comments: comments || undefined,
    });
    handleCancelClick();
  };

  const handleContinueClick = () => setShowReportReasonSelections(false);

  const handleSelectReason = (reason: ReportConversationReason) => {
    setSelectedReason(reason);
    return setShowAdditionalText(reason === ReportConversationReason.DiscriminatoryOrAbusive);
  };

  const handleCommentsInputChange = (details: string) => setComments(details);

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && handleCancelClick()}
      size={{
        base: 'fullScreen',
        md: 'md',
      }}
      header={<Heading level={1}>Report conversation</Heading>}
      body={
        showReportReasonSelections ? (
          <>
            <FieldSet
              legend={
                <Legend>
                  <Paragraph textStyle="body-lg-bold">Why do you want to report this conversation?</Paragraph>
                </Legend>
              }
            >
              {REPORT_REASON_SELECTIONS.map((selection) => (
                <LabeledControl
                  key={selection.value}
                  label={<Label>{selection.label}</Label>}
                  control={
                    <Radio
                      name="report-reason"
                      value={selection.value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleSelectReason(e.target.value as ReportConversationReason)
                      }
                    />
                  }
                />
              ))}
            </FieldSet>

            {showAdditionalText && (
              <Paragraph css={{ paddingTop: 'tighter' }}>
                We require our users to adhere to our{' '}
                {persona === 'landlord' ? (
                  <Anchor href="https://www.zillow.com/z/info/good-neighbor-policy/" target="_blank">
                    Good Neighbor Policy
                  </Anchor>
                ) : (
                  <Anchor href={paths.pledgeURL} target="_blank">
                    Respectful Renting Pledge
                  </Anchor>
                )}
                . If you believe this conversation violates this pledge, please let us know.
              </Paragraph>
            )}

            {selectedReason && (
              <FormField
                label={
                  <Label>
                    Add details
                    <Text textStyle="body-sm">&nbsp;(Optional)</Text>
                  </Label>
                }
                control={
                  <Textarea
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleCommentsInputChange(e.target.value)}
                    value={comments}
                    rows={2}
                    placeholder="Describe your issue here. Please be as specific as possible."
                  />
                }
                css={{ paddingTop: 'tighter' }}
              />
            )}
          </>
        ) : (
          <>
            <Paragraph textStyle="body-lg-bold">Report this conversation?</Paragraph>
            <Paragraph css={{ paddingTop: 'default' }}>
              Messages from {displayName} will be reported to {formattedBrandName} and reviewed by our moderators.
              Future messages from {displayName} will be sent to the &quot;
              {brand === 'zillow' && persona === 'renter' ? 'Spam' : 'Reported'}&quot; folder and may be blocked,
              pending moderator review.
            </Paragraph>
          </>
        )
      }
      footer={
        <ButtonGroup aria-label="report-conversation-modal-options">
          <TextButton tone="brand" onClick={handleCancelClick}>
            Cancel
          </TextButton>
          {showReportReasonSelections ? (
            <Button tone="brand" emphasis="filled" onClick={handleContinueClick} disabled={!selectedReason}>
              Continue
            </Button>
          ) : (
            <Button tone="critical" emphasis="filled" onClick={handleReportClick} disabled={isImpersonator}>
              Report
            </Button>
          )}
        </ButtonGroup>
      }
    />
  );
}
